import { useEffect, useRef } from 'react';
import {
	SESSION_IDLE_LOCK_MS,
	SESSION_IDLE_LOGOUT_MS,
} from '../constants/sessionIdle';
import { getLastActivityAt } from '../utils/sessionIdleStorage';

const LOCK_ROOT_SELECTOR = '[data-session-lock-root]';
const LOCK_SHIELD_SELECTOR = '[data-session-lock-shield]';

const isElementVisible = (element) => {
	if (!element || !element.isConnected) return false;

	const style = window.getComputedStyle(element);
	if (style.display === 'none' || style.visibility === 'hidden') return false;
	if (Number.parseFloat(style.opacity || '1') < 0.05) return false;
	if (style.pointerEvents === 'none') return false;

	const rect = element.getBoundingClientRect();
	return rect.width > 0 && rect.height > 0;
};

const isWithinLockWindow = () => {
	const idleMs = Date.now() - getLastActivityAt();
	return idleMs >= SESSION_IDLE_LOCK_MS && idleMs < SESSION_IDLE_LOGOUT_MS;
};

const isPastLogoutWindow = () => {
	return Date.now() - getLastActivityAt() >= SESSION_IDLE_LOGOUT_MS;
};

const isInsideLockUi = (target) =>
	Boolean(target?.closest?.(`${LOCK_ROOT_SELECTOR}, ${LOCK_SHIELD_SELECTOR}`));

const isLockInteraction = (event) => {
	if (isInsideLockUi(event.target)) return true;
	if (isInsideLockUi(document.activeElement)) return true;
	return false;
};

const isLockChromeElement = (element) => {
	if (!(element instanceof Element)) return false;
	return (
		element.matches(LOCK_ROOT_SELECTOR) ||
		element.matches(LOCK_SHIELD_SELECTOR) ||
		Boolean(element.querySelector(LOCK_ROOT_SELECTOR)) ||
		Boolean(element.querySelector(LOCK_SHIELD_SELECTOR))
	);
};

const mutationAffectsLockChrome = (mutation) => {
	if (mutation.type === 'attributes') {
		return mutation.target instanceof Element && isLockChromeElement(mutation.target);
	}

	if (mutation.type === 'childList') {
		const nodes = [...mutation.removedNodes, ...mutation.addedNodes];
		return nodes.some((node) => isLockChromeElement(node));
	}

	return false;
};

/**
 * Detects DevTools/CSS bypass attempts while the session is locked and
 * triggers reinforce (re-mount overlay) or logout when appropriate.
 */
export const useSessionLockTamperGuard = ({ active, onReinforceLock, onLogout }) => {
	const onReinforceRef = useRef(onReinforceLock);
	const onLogoutRef = useRef(onLogout);
	const handlingTamperRef = useRef(false);

	useEffect(() => {
		onReinforceRef.current = onReinforceLock;
	}, [onReinforceLock]);

	useEffect(() => {
		onLogoutRef.current = onLogout;
	}, [onLogout]);

	useEffect(() => {
		if (!active) return undefined;

		const handleTamper = () => {
			if (handlingTamperRef.current) return;
			handlingTamperRef.current = true;

			try {
				if (isPastLogoutWindow()) {
					onLogoutRef.current?.();
					return;
				}

				if (isWithinLockWindow()) {
					onReinforceRef.current?.();
				}
			} finally {
				window.setTimeout(() => {
					handlingTamperRef.current = false;
				}, 500);
			}
		};

		const verifyLockUi = () => {
			if (!active) return;

			const overlay = document.querySelector(LOCK_ROOT_SELECTOR);
			const shield = document.querySelector(LOCK_SHIELD_SELECTOR);

			if (
				!overlay ||
				!shield ||
				!overlay.isConnected ||
				!shield.isConnected ||
				!isElementVisible(overlay) ||
				!isElementVisible(shield)
			) {
				handleTamper();
			}
		};

		let guardReady = false;
		const readyTimer = window.setTimeout(() => {
			guardReady = true;
			verifyLockUi();
		}, 150);

		const blockOutsidePointer = (event) => {
			if (!active || isLockInteraction(event)) return;

			event.preventDefault();
			event.stopPropagation();
			handleTamper();
		};

		const blockOutsideFocus = (event) => {
			if (!active || isLockInteraction(event)) return;

			event.preventDefault();
			event.stopPropagation();
			handleTamper();
		};

		const pollId = window.setInterval(() => {
			if (guardReady) verifyLockUi();
		}, 1000);

		const observer = new MutationObserver((mutations) => {
			if (!guardReady) return;
			if (mutations.some(mutationAffectsLockChrome)) {
				verifyLockUi();
			}
		});
		observer.observe(document.documentElement, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
		});

		const pointerEvents = ['click', 'mousedown', 'touchstart'];
		pointerEvents.forEach((eventName) => {
			document.addEventListener(eventName, blockOutsidePointer, true);
		});
		document.addEventListener('focusin', blockOutsideFocus, true);

		return () => {
			window.clearTimeout(readyTimer);
			window.clearInterval(pollId);
			observer.disconnect();
			pointerEvents.forEach((eventName) => {
				document.removeEventListener(eventName, blockOutsidePointer, true);
			});
			document.removeEventListener('focusin', blockOutsideFocus, true);
		};
	}, [active]);
};
